import Rx from 'rx'
import { TypedRecord, recordify } from 'typed-immutable-record'
import { declareResourceType } from 'rwwa-data-access'
import * as apiService from '@classic/Foundation/Services/ApiService'
import type { LogonWithMfaResult, MFAServerMethod } from '@mobi/api-types'
import {
  ChangePasswordFailed,
  ChangePasswordSucceeded,
  ChangeTemporaryPasswordSucceeded,
} from '@core/Areas/ChangePassword/driver'
import { sendToNative } from '@mobi/web-native-comms/web'

export interface AccountDetails {
  isAuthorized: boolean
  accountNumber: string | null
}

const defaultAccountDetails: AccountDetails = {
  isAuthorized: false,
  accountNumber: null,
}

export interface AccountDetailsRecord extends TypedRecord<AccountDetailsRecord>, AccountDetails {}

export const ChangePasswordKey = 'me'
const ChangePasswordTypeKey = 'changePassword'

export const ChangePasswordRepo = declareResourceType<AccountDetailsRecord, AccountDetails>({
  typeKey: ChangePasswordTypeKey,
  // @ts-expect-error Legacy - use better data management solution
  fetchMultipleNoPromise,
  hardTimeToLive: 200,
  map: x => recordify<AccountDetails, AccountDetailsRecord>(x),
})

function fetchMultipleNoPromise(keys: string[]) {
  return Rx.Observable.fromPromise(
    fetchAccountDetails()
      .then(response => ({
        [ChangePasswordTypeKey]: {
          [ChangePasswordKey]: response,
        },
      }))
      .then(result => ({ keys, result }))
      .catch(() => ({
        keys,
        result: { [ChangePasswordTypeKey]: { [ChangePasswordTypeKey]: defaultAccountDetails } },
      }))
  )
}

export function fetchAccountDetails(): Promise<AccountDetails> {
  return apiService.get<AccountDetails>({ url: '/$_/api/Authentication/getAccountDetails' })
}

interface ChangeTemporaryPasswordDesktopRequest {
  betAccountId: string
  existingPassword: string
  newPassword: string
}

interface ChangePasswordRequest {
  accountNumber: string
  existingPassword: string
  newPassword: string
  confirmPassword: string
}

export interface MfaChallenge {
  mfaSessionId: string
  mfaFactor: MFAServerMethod
  mfaHint: string
}

export interface SimpleChangePasswordResponse {
  isChangePasswordSuccess: boolean
  error: string
  mustCompleteMfaChallenge: undefined | false
}

export interface ChangePasswordResponse {
  isChangePasswordSuccess: boolean
  error: string
  mustCompleteMfaChallenge: true
  mfaSessionId: string
  mfaFactor: MFAServerMethod
  mfaHint: string
}

interface CompleteChangePasswordRequest {
  accountNumber: string
  mfaSessionId: string
  mfaFactor: MFAServerMethod
  mfaCode: string
  currentPassword: string
  newPassword: string
}

export interface CompleteChangePasswordResponse {
  isChangePasswordSuccess: boolean
  error: string | null
}

export function changeTemporaryPasswordDesktopSubmit(
  model: ChangeTemporaryPasswordDesktopRequest
): Promise<SimpleChangePasswordResponse | ChangePasswordResponse> {
  return apiService.post<ChangePasswordResponse>({
    url: '/api/password/changeTemporary',
    body: model,
  })
}

export function changePasswordSubmit(
  model: ChangePasswordRequest,
  isDesktop: boolean
): Promise<SimpleChangePasswordResponse | ChangePasswordResponse> {
  return isDesktop
    ? apiService.post<ChangePasswordResponse>({ url: '/api/password/change', body: model })
    : apiService.post<ChangePasswordResponse>({
        url: '/$_/api/Authentication/ChangePassword',
        body: model,
      })
}

export function completeChangePasswordWithMfa(
  model: CompleteChangePasswordRequest
): Promise<ChangePasswordResponse> {
  return apiService.post<ChangePasswordResponse>({
    url: '/$_/api/Authentication/CompleteChangePasswordMfa',
    body: model,
  })
}

/**
 * Handles the response from a password change operation and dispatches appropriate signals
 * @param response The response from the password change API
 * @param options Config options for handling the response
 * @returns true if the operation was successful, false otherwise
 */
export function handleChangePasswordResponse(
  response: SimpleChangePasswordResponse | ChangePasswordResponse,
  options: {
    accountNumber: string
    newPassword: string
    form: HTMLFormElement
    isBiometricsEnabledByUser?: boolean
    isChangeTemporaryPassword?: boolean
    isDesktop?: boolean
    onMfaChallengeReceived?: (data: MfaChallenge) => void
    onSuccess?: () => void
  }
): boolean {
  const {
    accountNumber,
    newPassword,
    form,
    isBiometricsEnabledByUser,
    isChangeTemporaryPassword,
    isDesktop,
    onMfaChallengeReceived,
    onSuccess,
  } = options

  if (response.isChangePasswordSuccess) {
    if (isBiometricsEnabledByUser) {
      sendToNative('SET_BIOMETRICS_PASSWORD', {
        accountNumber: +accountNumber,
        password: newPassword,
      })
    }
  }

  if (response.isChangePasswordSuccess && !response.mustCompleteMfaChallenge) {
    if (isChangeTemporaryPassword) {
      ChangeTemporaryPasswordSucceeded({ form, isDesktop })
    } else {
      ChangePasswordSucceeded(form)
    }

    onSuccess?.()
    return true
  }

  if (response.mustCompleteMfaChallenge) {
    if (onMfaChallengeReceived) {
      onMfaChallengeReceived({
        mfaSessionId: response.mfaSessionId,
        mfaFactor: response.mfaFactor,
        mfaHint: response.mfaHint,
      })
      return true
    }
  }
  ChangePasswordFailed(response.error)
  return false
}

/**
 * Handles the MFA logon MFA result in the context of a password change
 */
export function handleLogonMfaResult(
  { isSuccessful }: LogonWithMfaResult,
  options: { form: HTMLFormElement; onSuccess?: () => void }
): boolean {
  if (isSuccessful) {
    const { form, onSuccess } = options
    ChangeTemporaryPasswordSucceeded({ form, isDesktop: false })
    onSuccess?.()
    return true
  }

  ChangePasswordFailed()
  return false
}
